import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Drop-in replacement for react-router's `useNavigate` that preserves
 * the `?embed=1` query param when the current URL has it.
 *
 * Thread this through any navigation that crosses between listing ↔ builder
 * (or any other internal route) so embed mode survives react-router pushes.
 */

const EMBED_RE = /(^|[?&])embed=1(&|$)/;

function withEmbed(to) {
  if (typeof to === 'number') return to;

  if (typeof to === 'string') {
    const [pathAndSearch, hash = ''] = to.split('#');
    if (EMBED_RE.test(pathAndSearch)) return to;
    const sep = pathAndSearch.includes('?') ? '&' : '?';
    const hashPart = hash ? `#${hash}` : '';
    return `${pathAndSearch}${sep}embed=1${hashPart}`;
  }

  // object form: { pathname, search, hash }
  const search = to?.search || '';
  if (EMBED_RE.test(search)) return to;
  const clean = search.replace(/^\?/, '');
  return {
    ...to,
    search: clean ? `?${clean}&embed=1` : '?embed=1'
  };
}

export function useEmbedAwareNavigate() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const isEmbed = EMBED_RE.test(search);

  return useCallback(
    (to, opts) => {
      if (!isEmbed) return navigate(to, opts);
      return navigate(withEmbed(to), opts);
    },
    [navigate, isEmbed]
  );
}
